import { ApiTagType, asky, baseApi, Http, Period, rtkq, RtkqRequest } from '@app/base';
import {
  Encounter,
  EncounterExaminationHistoryItem,
  EncounterSummaryHistoryItem,
  EncounterListItem,
  EncounterOverview,
  EncounterPreviousEncounter,
  EncounterReport,
  EncounterReportListItem,
  PastEncounterSummary,
  RecognizeSoapNotesRequest,
  UnbilledEncounterListItem,
  UpdateEncounterRequest,
} from './encountersTypes';
import {
  GetEncounterByAppointmentEventIdDocument,
  GetEncounterCountDocument,
  GetEncounterDocument,
  GetEncounterListDocument,
  GetEncounterOverviewDocument,
  GetEncounterReportDocument,
  GetEncounterReportListDocument,
  GetEncountersExaminationsDocument,
  GetEncountersSummariesDocument,
  GetPreviousEncounterDocument,
  GetPastEncounterSummaryDocument,
  GetUnbilledEncounterListDocument,
} from './graphql';

type UnbilledEncounterQueryArgs = { ids?: string[] };

export const encountersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEncounter: build.query<Encounter, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEncounterDocument, {
        select: (x) => x.encounter as Encounter,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'medical-record:encounter', id }],
    }),

    getEncounterList: build.query<
      EncounterListItem[],
      RtkqRequest<{
        period?: Period<string>;
        providerIds?: string[];
        patientIds?: string[];
        specialtyId?: string;
      }>
    >({
      queryFn: rtkq.graphql(GetEncounterListDocument, {
        vars: ({ period, providerIds, patientIds, specialtyId }) => ({
          period: period ?? null,
          patientIds: patientIds ?? null,
          providerIds: providerIds ?? null,
          specialtyIds: specialtyId ? [specialtyId] : null,
        }),
        select: (x) => x.encounters as EncounterListItem[],
      }),

      providesTags: (result = []) => [
        { type: 'medical-record:encounter', id: 'list' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:encounter' as const,
          id,
        })),
      ],
    }),

    getEncountersExaminations: build.query<
      EncounterExaminationHistoryItem[],
      RtkqRequest<{
        patientId: string;
        from?: string;
        to?: string;
      }>
    >({
      queryFn: rtkq.graphql(GetEncountersExaminationsDocument, {
        vars: ({ patientId, from, to }) => ({
          patientIds: [patientId],
          period:
            from && to
              ? {
                  start: from,
                  end: to,
                }
              : null,
        }),
        select: (x) => x.encounters as EncounterExaminationHistoryItem[],
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:encounter', id: 'examinations' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:encounter' as const,
          id,
        })),
      ],
    }),

    getEncountersSummaries: build.query<
      EncounterSummaryHistoryItem[],
      RtkqRequest<{
        patientId: string;
        from?: string;
        to?: string;
      }>
    >({
      queryFn: rtkq.graphql(GetEncountersSummariesDocument, {
        vars: ({ patientId, from, to }) => ({
          patientIds: [patientId],
          period:
            from && to
              ? {
                  start: from,
                  end: to,
                }
              : null,
        }),
        select: (x) => x.encounters as EncounterSummaryHistoryItem[],
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:encounter', id: 'summaries' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:encounter' as const,
          id,
        })),
      ],
    }),

    getEncounterReportList: build.query<
      EncounterReportListItem[],
      RtkqRequest<{
        period?: Period<string>;
        providerIds?: string[];
        patientIds?: string[];
        specialtyId?: string;
        careHomeIds?: string[];
      }>
    >({
      queryFn: rtkq.graphql(GetEncounterReportListDocument, {
        vars: ({ period, providerIds, patientIds, specialtyId, careHomeIds }) => ({
          period: period ?? null,
          patientIds: patientIds ?? null,
          providerIds: providerIds ?? null,
          specialtyIds: specialtyId ? [specialtyId] : null,
          careHomeIds: careHomeIds ?? null,
        }),
        select: (x) => x.encounters as EncounterReportListItem[],
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:encounter', id: 'report-list' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:encounter' as const,
          id,
        })),
      ],
    }),

    getEncounterByAppointmentEventId: build.query<Encounter, RtkqRequest<{ appointmentEventId: string }>>({
      queryFn: rtkq.graphql(GetEncounterByAppointmentEventIdDocument, {
        select: (x) => x.encounterByAppointmentEventId as Encounter,
        vars: ({ appointmentEventId }) => ({ appointmentEventId }),
      }),
      providesTags: (result) => [{ type: 'medical-record:encounter', id: result?.id ?? 'none' }],
    }),

    getEncounterOverview: build.query<EncounterOverview, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEncounterOverviewDocument, {
        select: (x) => x.encounter as EncounterOverview,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'medical-record:encounter', id }],
    }),

    getPastEncounterSummary: build.query<PastEncounterSummary, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetPastEncounterSummaryDocument, {
        select: (x) => x.encounter as PastEncounterSummary,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'medical-record:encounter', id }],
    }),

    getPreviousEncounter: build.query<EncounterPreviousEncounter, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetPreviousEncounterDocument, {
        select: (x) => x.encounter as EncounterPreviousEncounter,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (result) => [
        {
          type: 'medical-record:encounter',
          id: result?.previousEncounter?.id ?? 'none',
        },
      ],
    }),

    updateEncounter: build.mutation<void, RtkqRequest<UpdateEncounterRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/encounter/${args.id}`, args)),
      onQueryStarted: async ({ id, history, examination, summary, note }, { dispatch, queryFulfilled }) => {
        const rollback = dispatch(
          encountersApi.util.updateQueryData('getEncounter', { id: id }, (encounter) => {
            if (!encounter) return;
            if (history != null) encounter.history = history.value;
            if (examination != null)
              encounter.examination = {
                investigation: examination.value?.investigation ?? null,
                eyeHealth: examination.value?.eyeHealth ?? null,
                supplementaryTests: examination.value?.supplementaryTests ?? null,
                refractions: examination.value?.refractions ?? null,
              };
            if (summary != null) encounter.summary = summary.value;
            if (note != null) encounter.note = note.value;
          }),
        );

        queryFulfilled.catch(() => rollback.undo());
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'medical-record:encounter', id },
        { type: 'medical-record:encounter', id: 'report-list' },
        { type: 'medical-record:encounter', id: 'examinations' },
      ],
    }),

    getEncounterReport: build.query<EncounterReport, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEncounterReportDocument, {
        select: (x) => x.encounter as EncounterReport,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'medical-record:encounter', id }],
    }),

    getEncountersCount: build.query<
      number,
      RtkqRequest<{
        from?: string;
        to?: string;
        providerIds?: string[];
        patientIds?: string[];
      }>
    >({
      queryFn: rtkq.graphql(GetEncounterCountDocument, {
        vars: ({ to, from, providerIds, patientIds }) => {
          const filter = asky.filter.json([
            !!to && { fieldId: 'appointment.period.start', operator: '<', value: to },
            !!from && { fieldId: 'appointment.period.end', operator: '>', value: from },
            !!providerIds && { fieldId: 'providerId', operator: 'in', values: providerIds },
            !!patientIds && { fieldId: 'patientId', operator: 'in', values: patientIds },
          ]);

          return { filter };
        },
        select: (x) => x.encounterCount!,
      }),
      providesTags: ['medical-record:encounter', 'schedule:appointment-event' as ApiTagType],
    }),

    recognizeSoapNotes: build.mutation<string, RtkqRequest<RecognizeSoapNotesRequest>>({
      queryFn: rtkq.axios((args) => Http.post(`/api/encounter/recognize-soap-notes`, args)),
    }),

    getUnbilledEncounterList: build.query<
      UnbilledEncounterListItem[],
      RtkqRequest<UnbilledEncounterQueryArgs | void>
    >({
      queryFn: rtkq.graphql(GetUnbilledEncounterListDocument, {
        select: (x) => x.encounters!,
        vars: (args) => {
          const input = typeof args === 'object' ? (args as UnbilledEncounterQueryArgs) : {};
          return { ids: input.ids ?? null };
        },
      }),
      providesTags: ['medical-record:encounter', 'coverage:claim'],
    }),
  }),
});

export const {
  useGetEncounterByAppointmentEventIdQuery,
  useLazyGetEncounterByAppointmentEventIdQuery,
  useLazyGetEncounterListQuery,
  useGetEncounterQuery,
  useLazyGetEncounterQuery,
  useGetEncounterOverviewQuery,
  useUpdateEncounterMutation,
  useGetEncountersCountQuery,
  useGetEncounterListQuery,
  useGetPastEncounterSummaryQuery,
  useGetPreviousEncounterQuery,
  useLazyGetPreviousEncounterQuery,
  useGetEncounterReportListQuery,
  useGetEncounterReportQuery,
  useLazyGetEncounterReportQuery,
  useRecognizeSoapNotesMutation,
  useGetUnbilledEncounterListQuery,
  useGetEncountersExaminationsQuery,
  useGetEncountersSummariesQuery,
} = encountersApi;
